/**
 * CR314: useAssetUpload hook
 * 
 * Handles file validation, upload to Supabase Storage (app-assets bucket),
 * and creating the corresponding app_assets row.
 */

import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  ASSETS_BUCKET,
  ALLOWED_MIME_TYPES,
  SIZE_LIMITS,
  MAX_UPLOAD_SIZE_MB,
  type AllowedMimeType,
} from "@/lib/asset-studio/assetStorage";
import type { AssetType } from "@/lib/asset-studio/types";

// ================================================================
// TYPES
// ================================================================

export interface UploadResult {
  success: boolean;
  assetId?: string;
  url?: string;
  error?: string;
}

export interface UploadingFile {
  file: File;
  name: string;
  preview: string;
  progress: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: string;
  assetId?: string;
}

interface UseAssetUploadOptions {
  /** Default asset type for uploaded files */
  defaultType?: AssetType;
  /** Default tags to apply */
  defaultTags?: string[];
  /** Callback after successful upload */
  onSuccess?: (assetId: string) => void;
}

// ================================================================
// CONSTANTS
// ================================================================

/** Accepted file extensions for the file input */
export const ACCEPTED_FILE_TYPES = ".png,.jpg,.jpeg,.webp,.svg";

/** Human-readable list of accepted types */
export const ACCEPTED_TYPES_LABEL = "PNG, JPG, WebP, SVG";

// Minimal placeholder SVG for raster uploads (app_assets.svg is NOT NULL)
const RASTER_PLACEHOLDER_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"><rect width="1" height="1" fill="transparent"/></svg>`;

// File extension to MIME mapping
const EXT_TO_MIME: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  svg: "image/svg+xml",
};

// ================================================================
// VALIDATION
// ================================================================

export function validateUploadFile(file: File): { valid: boolean; error?: string } {
  // Check MIME type
  const mimeType = file.type || guessMimeType(file.name);
  if (!ALLOWED_MIME_TYPES.includes(mimeType as AllowedMimeType)) {
    return {
      valid: false,
      error: `Unsupported file type: ${file.type || "unknown"}. Accepted: ${ACCEPTED_TYPES_LABEL}`,
    };
  }

  // Check size
  const limit = SIZE_LIMITS[mimeType as AllowedMimeType];
  if (file.size > limit) {
    const limitMB = Math.round(limit / (1024 * 1024));
    const fileMB = (file.size / (1024 * 1024)).toFixed(1);
    return {
      valid: false,
      error: `File too large (${fileMB}MB). Max: ${limitMB}MB`,
    };
  }

  return { valid: true };
}

function guessMimeType(filename: string): string {
  const ext = filename.split(".").pop()?.toLowerCase() || "";
  return EXT_TO_MIME[ext] || "";
}

// ================================================================
// HOOK
// ================================================================

export function useAssetUpload(options: UseAssetUploadOptions = {}) {
  const { defaultType = "icon", defaultTags = [], onSuccess } = options;

  const [uploading, setUploading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);

  /**
   * Upload a single file: store in bucket + create app_assets row
   */
  const uploadFile = useCallback(
    async (
      file: File,
      overrides?: { name?: string; type?: AssetType; tags?: string[] }
    ): Promise<UploadResult> => {
      // Validate
      const validation = validateUploadFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return { success: false, error: validation.error };
      }

      const mimeType = file.type || guessMimeType(file.name);
      const isSvg = mimeType === "image/svg+xml";

      try {
        // Build storage path: asset-studio/images/{yyyy}/{mm}/{uuid}_{filename}
        const now = new Date();
        const yyyy = now.getFullYear();
        const mm = String(now.getMonth() + 1).padStart(2, "0");
        const uuid = crypto.randomUUID();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const storagePath = `asset-studio/images/${yyyy}/${mm}/${uuid}_${safeName}`;

        // Upload to storage
        const { error: uploadError } = await supabase.storage
          .from(ASSETS_BUCKET)
          .upload(storagePath, file, {
            upsert: false,
            contentType: mimeType,
            cacheControl: "3600",
          });

        if (uploadError) {
          console.error("[useAssetUpload] Storage upload failed:", uploadError);
          return { success: false, error: uploadError.message };
        }

        // Get public URL
        const { data: urlData } = supabase.storage
          .from(ASSETS_BUCKET)
          .getPublicUrl(storagePath);

        const publicUrl = urlData.publicUrl;

        // For SVG: read content for the svg column
        // For raster: use placeholder
        let svgContent = RASTER_PLACEHOLDER_SVG;
        if (isSvg) {
          svgContent = await file.text();
        }

        const assetName =
          overrides?.name ||
          file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " ");
        const assetType = overrides?.type || defaultType;
        const assetTags = overrides?.tags || defaultTags;

        // Insert app_assets row
        const { data: row, error: insertError } = await supabase
          .from("app_assets")
          .insert({
            name: assetName,
            type: assetType,
            svg: svgContent,
            url: storagePath, // Store the path key, resolved by getAppAssetPublicUrl
            mime_type: mimeType,
            size_bytes: file.size,
            tags: assetTags,
            status: "active",
          })
          .select("id")
          .single();

        if (insertError) {
          console.error("[useAssetUpload] DB insert failed:", insertError);
          // Cleanup: remove uploaded file
          await supabase.storage.from(ASSETS_BUCKET).remove([storagePath]);
          return { success: false, error: insertError.message };
        }

        return { success: true, assetId: row.id, url: publicUrl };
      } catch (err) {
        console.error("[useAssetUpload] Exception:", err);
        return {
          success: false,
          error: err instanceof Error ? err.message : "Upload failed",
        };
      }
    },
    [defaultType, defaultTags]
  );

  /**
   * Upload multiple files with progress tracking
   */
  const uploadFiles = useCallback(
    async (
      files: File[],
      overrides?: { type?: AssetType; tags?: string[] }
    ) => {
      setUploading(true);

      // Initialize tracking
      const tracking: UploadingFile[] = files.map((file) => ({
        file,
        name: file.name,
        preview: URL.createObjectURL(file),
        progress: 0,
        status: "pending" as const,
      }));
      setUploadingFiles([...tracking]);

      let successCount = 0;

      for (let i = 0; i < tracking.length; i++) {
        tracking[i].status = "uploading";
        tracking[i].progress = 30;
        setUploadingFiles([...tracking]);

        const result = await uploadFile(tracking[i].file, overrides);

        if (result.success) {
          tracking[i].status = "success";
          tracking[i].progress = 100;
          tracking[i].assetId = result.assetId;
          successCount++;
          onSuccess?.(result.assetId!);
        } else {
          tracking[i].status = "error";
          tracking[i].error = result.error;
        }
        setUploadingFiles([...tracking]);
      }

      if (successCount > 0) {
        toast.success(
          `${successCount} asset${successCount > 1 ? "s" : ""} uploaded`
        );
      }

      setUploading(false);

      // Cleanup preview URLs after a delay
      setTimeout(() => {
        tracking.forEach((t) => URL.revokeObjectURL(t.preview));
      }, 3000);
    },
    [uploadFile, onSuccess]
  );

  const clearUploadingFiles = useCallback(() => {
    setUploadingFiles([]);
  }, []);

  return {
    uploading,
    uploadingFiles,
    uploadFile,
    uploadFiles,
    clearUploadingFiles,
    validateUploadFile,
  };
}
